import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PenLine, Loader2, Save } from "lucide-react";

// V1.33-X · Única fuente de configuración para TODAS las firmas remotas
// (contratos, documentos DGT, consentimientos, autorizaciones).
const METHODS = [
  { value: "direct", label: "Firma Directa", help: "El alumno lee, firma en pantalla y acepta con checkbox. Sin códigos." },
  { value: "email", label: "Validación por Email", help: "Tras firmar se envía un código al email del firmante." },
  { value: "sms", label: "Validación por SMS", help: "Tras firmar se envía un código OTP al móvil." },
  { value: "email_or_sms", label: "Email o SMS (a elección del alumno)", help: "El firmante elige el canal y puede cambiar entre ambos." },
];

export default function SignatureMethodSettings() {
  const [method, setMethod] = useState("direct");
  const [allowOverride, setAllowOverride] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/signature-config")
      .then(({ data }) => {
        setMethod(data.default_method || "direct");
        setAllowOverride(!!data.allow_franchise_override);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/signature-config", {
        default_method: method,
        allow_franchise_override: allowOverride,
      });
      toast.success("Configuración de firma guardada");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const selected = METHODS.find((m) => m.value === method);

  return (
    <div className="card-surface p-4 space-y-3" data-testid="signature-method-settings">
      <div className="flex items-center gap-2">
        <PenLine className="h-4 w-4 text-orange-600" />
        <h4 className="font-semibold text-sm">Firma Digital · Método por defecto</h4>
      </div>
      <p className="text-xs text-neutral-500">
        Se aplica a <b>todas las firmas remotas</b> del sistema: contratos, documentos DGT,
        consentimientos y autorizaciones. Única fuente de configuración (Matriz).
      </p>
      {loading ? (
        <div className="text-sm text-neutral-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : (
        <>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Método de firma por defecto</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              data-testid="signature-method-select"
              className="mt-1 w-full sm:w-96 py-2 px-3 rounded-lg border border-neutral-300 bg-white text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
            >
              {METHODS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            {selected && <p className="text-[11px] text-neutral-500 mt-1">{selected.help}</p>}
          </div>
          <label className="flex items-start gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={allowOverride}
              onChange={(e) => setAllowOverride(e.target.checked)}
              data-testid="signature-allow-override"
              className="h-4 w-4 mt-0.5 accent-orange-500"
            />
            <span>
              Permitir que cada franquicia elija su propio método
              <span className="block text-[10px] text-neutral-400">
                Jerarquía Matriz → Franquicia. Mientras no existan franquicias se usa siempre esta configuración.
              </span>
            </span>
          </label>
          <div className="flex justify-end">
            <button type="button" onClick={save} disabled={saving} className="btn-primary !h-9 !text-xs" data-testid="signature-method-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Guardar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
